import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@worthy-npm/worthy-common-ui-components';
import axios from 'axios';
import _ from 'lodash';
import WorthyAPI, { MediumTypes } from '../../../services/worthyAPI';
import {
  IRegistration, addActiveField,
} from '../../../slices/submitSlice';
import {
  DigitTextField,
  EmailBox, LabelTypography, LoginBox,
  StyledAlert,
  StyledTextField,
} from './step_registration.styles';
import { normalizePhoneNumber } from './step_registration_form';

export interface IStepRegistrationLoginFormProps {
  /** Handler for submitting registration/login data */
  onLogin: (data: IRegistration) => void;
  /** Initial value for email or phone number */
  initialValue: string;
  /** Type of the initial value */
  valueType: 'email' | 'phone';
  /** Flag indicating whether verification view is active */
  verificationView: boolean;
  /** Name of the current step */
  stepName: string;
  /** Index of the current step */
  idx: number;
  /** Whether to show promotional terms */
  showPromoTerms?: boolean; // eslint-disable-line react/require-default-props
  /** Whether the value field should be read-only */
  readOnlyValue?: boolean; // eslint-disable-line react/require-default-props
}

export function StepRegistrationLoginForm({
  onLogin,
  initialValue,
  valueType,
  showPromoTerms = false,
  readOnlyValue = false,
  ...props
}: IStepRegistrationLoginFormProps) {
  const dispatch = useDispatch();
  const [value, setValue] = useState<string>(initialValue);
  const [code, setCode] = useState<string>('');
  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoginLoading, setIsLoginLoading] = useState<boolean>(false);
  const CODE_LENGTH = 4;

  // Send OTP via authenticator API
  const handleSendCode = async () => {
    if (!value) {
      setError(`Please enter your ${valueType}`);
      return;
    }
    setIsLoginLoading(true);
    setError('');
    try {
      const { requestId: requestIdFromApi } = await WorthyAPI.sendOtpCode({
        email: valueType === 'email' ? value : '',
        phone: valueType === 'phone' ? value : '',
        medium: valueType === 'email' ? MediumTypes.anyMedium : MediumTypes.sms,
      });
      if (!requestIdFromApi) {
        throw new Error('no request id');
      }
      setRequestId(requestIdFromApi);
      setCodeSent(true);
    } catch (err) {
      const message = 'Unable to send code. Please try again.';
      setError(message);
      console.error('Error sending code', err); // eslint-disable-line no-console
    }
    setIsLoginLoading(false);
  };

  // Verify OTP via authenticator API
  const handleLogin = async () => {
    if (code.length < CODE_LENGTH) {
      setError(`Please enter the ${CODE_LENGTH}-digit code`);
      return;
    }
    setIsLoginLoading(true);
    setError('');
    try {
      await WorthyAPI.signInWithCode(value, code, requestId, valueType === 'email' ? 'anyMedium' : 'sms');
      await WorthyAPI.performMonolithSignin();
      const data: IRegistration = {
        firstName: '', lastName: '', email: value, phoneNumber: '',
      };
      onLogin(data);
    } catch (err) {
      const message = 'Invalid code. Please check and try again.';
      setError(message);
      console.error('Error logging in', err); // eslint-disable-line no-console
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleResend = () => {
    setCode('');
    setCodeSent(false);
    setError('');
  };

  // Automatically focus the first OTP input when codeSent becomes true
  React.useEffect(() => {
    if (codeSent) {
      const firstInput = document.getElementById('otp-input-0');
      if (firstInput) {
        (firstInput as HTMLInputElement).focus();
      }
    }
  }, [codeSent]);

  return (
    <LoginBox className="login-box">
      {!codeSent ? (
        <>
          <EmailBox textAlign="center">
            <LabelTypography variant="body1" mb={2}>
              {readOnlyValue || initialValue.length > 0
                ? `We'll send an activation 4 digit code to your ${_.capitalize(valueType)}`
                : `Please enter your ${valueType} and we'll send you an activation 4 digit code`}
            </LabelTypography>
            <StyledTextField
              fullWidth
              variant="standard"
              type={valueType === 'email' ? 'email' : 'tel'}
              value={valueType === 'email' ? value : normalizePhoneNumber(value)}
              onChange={(e) => {
                if (!readOnlyValue) {
                  setValue(e.target.value);
                }
              }}
              onPaste={() => dispatch(addActiveField(valueType))}
              required
              placeholder={valueType === 'email' ? 'Email' : '(423) 456-7890'}
              inputProps={{
                inputMode: valueType === 'email' ? 'email' : 'tel',
                readOnly: readOnlyValue,
              }}
            />
          </EmailBox>
          {error && <StyledAlert severity="error">{error}</StyledAlert>}
          <Box sx={{ alignSelf: 'stretch' }}>
            <Button
              onClick={() => handleSendCode()}
              disableElevation
              fullWidth
              type="submit"
              data-automation="login-button"
              variant="contained"
              size="large"
              color="highlight"
              sx={{
                fontWeight: 600, fontSize: 18, lineHeight: '22px', padding: '12px 8px 14px',
              }}
              disabled={isLoginLoading || !value}
            >
              Send
              {isLoginLoading && (
                <Box
                  component="span"
                  sx={{ ml: 2, display: 'inline-flex', verticalAlign: 'middle' }}
                >
                  <CircularProgress size={20} color="inherit" />
                </Box>
              )}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              width: '320px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center',
            }}
            className="label-and-email-sent"
          >
            <Box textAlign="center">
              <LabelTypography variant="body1" align="center">
                Enter the 4 digit login code sent to:
              </LabelTypography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: '#222B36', textAlign: 'center', fontWeight: 400, fontSize: '16px',
              }}
            >
              {valueType === 'phone' ? normalizePhoneNumber(value) : value}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" gap="8px" className="code-digits">
            {[0, 1, 2, 3].map((idx) => (
              <DigitTextField
                className={code[idx] ? 'digit-input' : 'digit-input-empty'}
                key={idx}
                variant="outlined"
                value={code[idx] || ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 1);
                  if (!val) return;
                  const newCode = code.split('');
                  newCode[idx] = val;
                  setCode(newCode.join('').slice(0, CODE_LENGTH));
                  // Move focus to next input
                  const next = document.getElementById(`otp-input-${idx + 1}`);
                  if (next) next.focus();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace') {
                    e.preventDefault();
                    const newCodeArr = code.split('');
                    if (newCodeArr[idx]) {
                      newCodeArr[idx] = '';
                    } else if (idx > 0) {
                      newCodeArr[idx - 1] = '';
                      const prev = document.getElementById(`otp-input-${idx - 1}`);
                      if (prev) prev.focus();
                    }
                    setCode(newCodeArr.join('').slice(0, CODE_LENGTH));
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = e.clipboardData.getData('Text');
                  const digits = paste.replace(/\D/g, '').split('').slice(0, CODE_LENGTH);
                  if (digits.length) {
                    const newCodeArr = code.split('');
                    digits.forEach((d: string, i: number) => {
                      if (idx + i < CODE_LENGTH) newCodeArr[idx + i] = d;
                    });
                    const newCode = newCodeArr.join('').slice(0, CODE_LENGTH);
                    setCode(newCode);
                    const focusIdx = Math.min(idx + digits.length, CODE_LENGTH - 1);
                    const next = document.getElementById(`otp-input-${focusIdx}`);
                    if (next) next.focus();
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 1,
                  style: {
                    padding: 0,
                    textAlign: 'center',
                  },
                }}
                id={`otp-input-${idx}`}
              />
            ))}
          </Box>
          {error && <StyledAlert severity="error">{error}</StyledAlert>}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              alignSelf: 'stretch',
              gap: '8px',
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              disabled={code.length < CODE_LENGTH || isLoginLoading}
              disableElevation
              type="submit"
              data-automation="submit-code-button"
              size="large"
              color="highlight"
              sx={{
                fontWeight: 600, fontSize: 18, lineHeight: '22px', padding: '12px 8px 14px',
              }}
            >
              Submit
              {isLoginLoading && (
                <Box
                  component="span"
                  sx={{ ml: 2, display: 'inline-flex', verticalAlign: 'middle' }}
                >
                  <CircularProgress size={20} color="inherit" />
                </Box>
              )}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={handleResend}
              disabled={isLoginLoading}
              size="large"
              color="highlight"
            >
              Resend code
            </Button>
          </Box>
        </>
      )}
    </LoginBox>
  );
}
